/**
 * Contact.
 *  - Admin mode: the member's contact list ("Contact details": phones and emails with labels, uses and one main per
 *    use) and custom fields ("More details"), membermanagement 003. The main Calling number and main email are the
 *    member's cellphone and email everywhere else in CCMS. When the contact list cannot be loaded (for example an
 *    API that is not updated yet) the four-field form below is shown as well.
 *  - Self mode: email, cellphone, telephone and an alternative contact. The email is read-only: it belongs to the
 *    login (PUT /me ignores it).
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { IconDeviceFloppy, IconRestore, IconAlertCircle, IconMail, IconPhone, IconDeviceMobile } from '@tabler/icons-react';
import { Card, Stack, Group, Button, TextInput, SimpleGrid, Text, Alert } from '@/components/ui';
import { useMemberService, type UpdateMemberRequest } from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import { useMemberPatch } from '../useMemberMutations';
import { textOrNull, errorMessage } from '../memberFormat';
import { ContactListCard } from './ContactListCard';
import { MoreDetailsCard } from './MoreDetailsCard';

interface ContactForm {
  email: string;
  cellNumber: string;
  telephone: string;
  altContact: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactTab(props: MemberTabProps) {
  const { api, member } = props;
  const memberService = useMemberService();
  const isAdmin = api.mode === 'admin';
  // Same query (and cache entry) as ContactListCard: only used to fall back to the four-field form.
  const points = useQuery({
    queryKey: api.keys.contactPoints,
    queryFn: () => memberService.getContactPoints(member.memberID),
    enabled: isAdmin && member.memberID > 0,
  });

  if (!isAdmin) return <LegacyContactForm {...props} />;
  return (
    <Stack gap="md">
      <ContactListCard {...props} />
      {points.isError && (
        <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="Showing the main contact details only">
          The contact list could not be loaded, so the four main contact fields are shown instead.
        </Alert>
      )}
      {points.isError && <LegacyContactForm {...props} />}
      <MoreDetailsCard {...props} />
    </Stack>
  );
}

/** The four legacy contact columns (self mode and the admin fallback). */
function LegacyContactForm({ api, member, perms }: MemberTabProps) {
  const patch = useMemberPatch(api);
  const initial = useMemo<ContactForm>(() => ({
    email: member.email ?? '',
    cellNumber: member.cellNumber ?? '',
    telephone: member.telephone ?? '',
    altContact: member.altContact ?? '',
  }), [member]);
  const [form, setForm] = useState<ContactForm>(initial);
  useEffect(() => setForm(initial), [initial]);

  const canEditEmail = api.mode === 'admin' && perms.editAdmin;
  const readOnly = !perms.edit;

  const changes = useMemo<UpdateMemberRequest>(() => {
    const c: UpdateMemberRequest = {};
    // Email: "" clears it (null would mean "unchanged" to the API).
    if (canEditEmail && form.email.trim() !== (member.email ?? '').trim()) c.email = form.email.trim();
    if ((textOrNull(form.cellNumber) ?? '') !== (member.cellNumber ?? '').trim()) c.cellNumber = textOrNull(form.cellNumber);
    if ((textOrNull(form.telephone) ?? '') !== (member.telephone ?? '').trim()) c.telephone = textOrNull(form.telephone);
    if ((textOrNull(form.altContact) ?? '') !== (member.altContact ?? '').trim()) c.altContact = textOrNull(form.altContact);
    return c;
  }, [form, member, canEditEmail]);

  const emailError = canEditEmail && form.email.trim() && !EMAIL_RE.test(form.email.trim()) ? 'Enter a valid email address' : null;
  const dirty = Object.keys(changes).length > 0;

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <div>
          <Text fw={600}>Contact details</Text>
          <Text size="sm" c="dimmed">How the church can reach {api.mode === 'self' ? 'you' : 'this person'}.</Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <TextInput
            label="Email"
            leftSection={<IconMail size={16} />}
            value={form.email}
            disabled={!canEditEmail}
            error={emailError}
            description={api.mode === 'self' ? 'Your email comes from your login. Change it on the Account tab.' : undefined}
            onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
          />
          <TextInput label="Cellphone number" placeholder="e.g. 082 123 4567" leftSection={<IconDeviceMobile size={16} />}
            value={form.cellNumber} disabled={readOnly} onChange={(e) => setForm({ ...form, cellNumber: e.currentTarget.value })} />
          <TextInput label="Telephone" leftSection={<IconPhone size={16} />} value={form.telephone} disabled={readOnly}
            onChange={(e) => setForm({ ...form, telephone: e.currentTarget.value })} />
          <TextInput label="Alternative contact" placeholder="Another number to reach you on" value={form.altContact} disabled={readOnly}
            onChange={(e) => setForm({ ...form, altContact: e.currentTarget.value })} />
        </SimpleGrid>

        {patch.isError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Not saved">{errorMessage(patch.error)}</Alert>
        )}

        {!readOnly && (
          <Group justify="flex-end">
            <Button variant="default" leftSection={<IconRestore size={16} />} disabled={!dirty || patch.isPending}
              onClick={() => { setForm(initial); patch.reset(); }}>
              Discard changes
            </Button>
            <Button leftSection={<IconDeviceFloppy size={16} />} disabled={!dirty || !!emailError} loading={patch.isPending}
              onClick={() => patch.mutate(changes)}>
              Save contact details
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}
